import React, { useMemo } from 'react';

import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Box, Stack, Typography, useTheme } from '@mui/material';

import type { MarketingRouterSignificanceTimeseriesDay } from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import { formatVisitors } from './marketingRouterSignificanceDisplay';
import { buildVisitorsChart, formatTrendDate } from './marketingRouterTrend';

/**
 * New visitors bucketed per day, one bar per variant. Uneven bars are the
 * first place a broken split or a paused campaign shows up.
 */
export default function MarketingRouterVisitorsChart({
  days,
  baseVariantId,
}: {
  days: MarketingRouterSignificanceTimeseriesDay[];
  baseVariantId: string;
}) {
  const theme = useTheme();
  const chart = useMemo(() => buildVisitorsChart(days, baseVariantId), [days, baseVariantId]);
  const treatmentColours = [theme.palette.warning.main, theme.palette.success.main, theme.palette.secondary.main];
  let treatmentIndex = 0;

  return (
    <Stack spacing={1.5}>
      <Typography variant="h2">New visitors per day</Typography>
      {chart.rows.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No daily data yet.
        </Typography>
      ) : (
        <Box sx={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={chart.rows} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
              <XAxis dataKey="date" tickFormatter={formatTrendDate} tick={{ fontSize: 12 }} minTickGap={24} />
              <YAxis tickFormatter={(value: number) => formatVisitors(value)} tick={{ fontSize: 12 }} width={56} />
              <Tooltip
                labelFormatter={(label) => formatTrendDate(String(label))}
                formatter={(value, name) => [typeof value === 'number' ? formatVisitors(value) : '—', String(name)]}
              />
              <Legend />
              {chart.series.map((series) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.label}
                  fill={
                    series.isControl
                      ? theme.palette.primary.main
                      : treatmentColours[treatmentIndex++ % treatmentColours.length]
                  }
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Stack>
  );
}
